import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Supervisor {
  id: string;
  code: string;
  role: string;
  fullNameEn: string;
  fullNameAr: string | null;
  phone: string;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
}

const fmtDate = (s: string | null) => s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function SupervisorsPage() {
  const [list, setList] = useState<Supervisor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<Supervisor[]>('/api/v1/construction/supervisors');
      setList(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/construction">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </Link>
          <div className="text-sm font-medium">Site supervisors</div>
          <Button size="sm" className="gap-2" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && !loading && (
          <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm p-3">{error}</div>
        )}

        {!loading && !error && list.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            <UserCog className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No supervisors yet. Tap Add to create the first.
          </div>
        )}

        <div className="space-y-2">
          {list.map((s) => (
            <div key={s.id} className="border border-border rounded-lg p-3">
              <div className="flex items-baseline justify-between">
                <div className="font-medium">{s.fullNameEn}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.code}</div>
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                <span>{s.phone}</span>
                <span>·</span>
                <span>Status {s.status}</span>
                <span>·</span>
                <span>Last login {fmtDate(s.lastLoginAt)}</span>
              </div>
            </div>
          ))}
        </div>
      </main>

      <AddSupervisorSheet open={addOpen} onClose={() => setAddOpen(false)} onCreated={refresh} />
    </div>
  );
}

function AddSupervisorSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [fullNameEn, setFullNameEn] = useState('');
  const [phone, setPhone] = useState('+966');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setFullNameEn(''); setPhone('+966'); setError(null); };

  const handleSubmit = async () => {
    setError(null);
    if (fullNameEn.trim().length < 2) { setError('Name must be at least 2 characters'); return; }
    if (!/^\+966[0-9]{9}$/.test(phone.trim())) { setError('Phone must be Saudi format +9665XXXXXXXX'); return; }
    setBusy(true);
    try {
      await apiClient.post('/api/v1/construction/supervisors', {
        fullNameEn: fullNameEn.trim(),
        phone: phone.trim(),
      });
      reset();
      onCreated();
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Add supervisor
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="sup-name">Full name</Label>
            <Input
              id="sup-name"
              autoFocus
              value={fullNameEn}
              onChange={(e) => setFullNameEn(e.target.value)}
              placeholder="e.g. Ahmed Al-Saud"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sup-phone">Phone</Label>
            <Input
              id="sup-phone"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+9665XXXXXXXX"
            />
            <p className="text-xs text-muted-foreground">They'll log in by SMS OTP. Saudi numbers only — format +966 followed by 9 digits.</p>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm p-3">
              {error}
            </div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={busy}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={busy} className="gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
